import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import moment from "moment";
import strings from "translate/language";
import { followupList } from "reduxStore/action/followUpActions";
import { getFollowupStatus } from "reduxStore/action/masterActions";
import { handleListResponse, seo } from "utils/utils";
import TableDataNotFound from "component/TableDataNotFound";
import Breadcrumb from "component/common/breadcrumb";
import Pagination from "component/common/Pagination";
import usePermission from "utils/usePermission";
import usePage from "utils/usePage";
import "./FollowupListing.css";

const PAGE_SIZE = 15;

const FOLLOWUP_FOR_OPTIONS = [
  { value: "1", label: strings.lead },
  { value: "2", label: strings.siteVisit },
  { value: "3", label: strings.booking },
  { value: "4", label: strings.registration },
];

const selectPortalProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 10060 }),
  },
};

const getDefaultDateRange = () => {
  const today = moment().format("YYYY-MM-DD");
  return { start_date: today, end_date: today };
};

const FollowUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const defaultDates = getDefaultDateRange();

  const [formValid, setFormValid] = useState({});
  const [endDateDisable, setEndDateDisable] = useState(true);
  const [followupListData, setFollowupListData] = useState([]);
  const [page, setPage] = usePage();
  const [followupStatusData, setFollowupStatusData] = useState([]);
  const [form, setForm] = useState({
    start_date: defaultDates.start_date,
    end_date: defaultDates.end_date,
    followup_status: "",
    followup_for: "",
  });

  const appliedFiltersRef = useRef({
    start_date: defaultDates.start_date,
    end_date: defaultDates.end_date,
    followup_status: "",
    followup_for: "",
  });

  const followupListSelector = useSelector((state) => state?.followUp) || [];
  const followupStatus = useSelector((state) => state.followupstatus);

  const { view, edit, isAdmin } = usePermission({
    view: "view_follow",
    edit: "edit_follow",
  });

  const statusOptions = useMemo(
    () =>
      (followupStatusData || []).map((item) => ({
        value: item._id,
        label: item.title,
      })),
    [followupStatusData]
  );

  const totalCount = Number(followupListSelector?.response?.total_data) || 0;

  const buildListParams = (offset = 0, source = form) => {
    const { start_date, end_date } =
      source.start_date && source.end_date ? source : getDefaultDateRange();
    return {
      start_date,
      end_date,
      followup_status: source.followup_status || "",
      followup_for: source.followup_for || "",
      limit: PAGE_SIZE,
      offset,
    };
  };

  useEffect(() => {
    const response = followupListSelector?.response;
    if (!response) return;
    if (response.status === 200) {
      const rows = response?.data;
      setFollowupListData(
        Array.isArray(rows) && rows.length > 0 ? handleListResponse(rows) : []
      );
      return;
    }
    if (!followupListSelector?.detail) {
      setFollowupListData([]);
    }
  }, [followupListSelector]);

  useEffect(() => {
    dispatch(
      followupList({
        ...appliedFiltersRef.current,
        limit: PAGE_SIZE,
        offset: page,
      })
    );
  }, [dispatch, page]);

  useEffect(() => {
    dispatch(getFollowupStatus());
  }, []);

  useEffect(() => {
    if (followupStatus?.response?.status === 200) {
      setFollowupStatusData(followupStatus.response.data || []);
    } else {
      setFollowupStatusData([]);
    }
  }, [followupStatus]);

  useEffect(() => {
    setEndDateDisable(!form.start_date);
  }, [form.start_date]);

  useEffect(() => {
    seo({
      title: "Followup Listing",
      metaDescription: "Followup listing",
      canonicalLink: window.location.href,
    });
  }, []);

  const handleValid = () => {
    const errors = { ...formValid };
    let formError = false;

    if (form.start_date && !form.end_date) {
      formError = true;
      errors.end_date = strings.endDateErr;
    }

    setFormValid(errors);
    return formError;
  };

  const filterDetail = () => {
    if (handleValid()) return;
    const params = buildListParams(0);
    appliedFiltersRef.current = params;
    if (page !== 0) {
      setPage(0);
      return;
    }
    dispatch(followupList(params));
  };

  const filterReset = () => {
    const { start_date, end_date } = getDefaultDateRange();
    const resetForm = {
      start_date,
      end_date,
      followup_status: "",
      followup_for: "",
    };
    setForm(resetForm);
    setFormValid({});
    const params = buildListParams(0, resetForm);
    appliedFiltersRef.current = params;
    if (page !== 0) {
      setPage(0);
      return;
    }
    dispatch(followupList(params));
  };

  const handlePageClick = (newOffset) => {
    setPage(newOffset);
  };

  const handleInputField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "end_date") {
      setFormValid((prev) => ({ ...prev, end_date: "" }));
    }
  };

  const handleFollowupForChange = (selected) => {
    setForm((prev) => ({
      ...prev,
      followup_for: selected?.value || "",
    }));
  };

  const handleFollowupStatusChange = (selected) => {
    setForm((prev) => ({
      ...prev,
      followup_status: selected?.value || "",
    }));
  };

  const selectedFollowupFor =
    FOLLOWUP_FOR_OPTIONS.find(
      (item) => item.value === String(form.followup_for)
    ) || null;

  const selectedFollowupStatus =
    statusOptions.find((item) => item.value === form.followup_status) || null;

  return (
    <div className="page-body justo-followup-page">
      <Breadcrumb title={strings.followup} titleactive={strings.followupList} />

      <div className="container-fluid justo-followup">
        <div className="justo-followup__card">
          <div className="justo-followup__top">
            <div>
              <h2 className="justo-followup__title">
                {strings.listOfFollowup}
              </h2>
              <p className="justo-followup__subtitle">
                Filter by date, type, and status · paginated results.
              </p>
            </div>
            <div className="justo-followup__count">
              Total <strong>{totalCount}</strong>
            </div>
          </div>

          <div className="justo-followup__filters">
            <div className="justo-followup__filters-grid">
              <div className="justo-followup__field">
                <label className="justo-followup__label" htmlFor="start_date">
                  {strings.startDate}
                </label>
                <input
                  id="start_date"
                  className="justo-followup__input"
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleInputField}
                />
              </div>
              <div className="justo-followup__field">
                <label className="justo-followup__label" htmlFor="end_date">
                  {strings.endDate}
                </label>
                <input
                  id="end_date"
                  className="justo-followup__input"
                  type="date"
                  name="end_date"
                  value={form.end_date}
                  onChange={handleInputField}
                  min={form.start_date}
                  disabled={endDateDisable}
                />
                {formValid.end_date ? (
                  <span className="justo-followup__error">
                    {formValid.end_date}
                  </span>
                ) : null}
              </div>
              <div className="justo-followup__field">
                <label className="justo-followup__label">
                  {strings.searchByType}
                </label>
                <Select
                  classNamePrefix="justo-followup-select"
                  isSearchable
                  isClearable
                  placeholder={strings.selectType}
                  options={FOLLOWUP_FOR_OPTIONS}
                  value={selectedFollowupFor}
                  onChange={handleFollowupForChange}
                  {...selectPortalProps}
                />
              </div>
              <div className="justo-followup__field">
                <label className="justo-followup__label">
                  {strings.searchByStatus}
                </label>
                <Select
                  classNamePrefix="justo-followup-select"
                  isSearchable
                  isClearable
                  placeholder={
                    statusOptions.length
                      ? strings.selectstatus
                      : strings.Loading
                  }
                  options={statusOptions}
                  value={selectedFollowupStatus}
                  onChange={handleFollowupStatusChange}
                  isDisabled={!statusOptions.length}
                  {...selectPortalProps}
                />
              </div>
            </div>
            <div className="justo-followup__filter-actions">
              <button
                type="button"
                className="justo-followup__btn justo-followup__btn--solid"
                onClick={filterDetail}
              >
                <i className="fa fa-search" aria-hidden="true" />
                {strings.search}
              </button>
              <button
                type="button"
                className="justo-followup__btn justo-followup__btn--ghost"
                onClick={filterReset}
              >
                {strings.reset}
              </button>
            </div>
          </div>

          <div className="justo-followup__table-wrap">
            <div className="justo-followup__scroll">
              <table className="justo-followup__table">
                <thead>
                  <tr>
                    <th scope="col">{strings.propertyName}</th>
                    <th scope="col">{strings.createdDate}</th>
                    <th scope="col">{strings.status}</th>
                    <th scope="col">{strings.followupDateTime}</th>
                    <th scope="col">{strings.visitorName}</th>
                    <th className="justo-followup__col-score" scope="col">
                      {strings.visitingScore}
                    </th>
                    <th className="justo-followup__col-action" scope="col">
                      {strings.action}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {followupListData.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <span className="justo-followup__property">
                          {item?.property_title || "—"}
                        </span>
                      </td>
                      <td className="justo-followup__muted">
                        {item.followup_date
                          ? moment(item.followup_date).format(
                              "DD-MM-YYYY hh:mm A"
                            )
                          : "—"}
                      </td>
                      <td>
                        {item.followup_status ? (
                          <span className="justo-followup__status">
                            <i className="fa fa-phone" aria-hidden="true" />
                            <span className="justo-followup__status-label">
                              {item.followup_status}
                            </span>
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="justo-followup__muted">
                        {item.next_followup_date
                          ? `${moment(item.next_followup_date).format(
                              "DD-MM-YYYY"
                            )} · ${item.followup_time || "—"}`
                          : "—"}
                      </td>
                      <td>
                        <span className="justo-followup__visitor">
                          {item.customer_first_name || "—"}
                        </span>
                      </td>
                      <td className="justo-followup__col-score">
                        <span className="justo-followup__score">
                          {item.visit_score ?? "—"}
                        </span>
                      </td>
                      <td className="justo-followup__col-action">
                        <div className="justo-followup__actions">
                          {view ? (
                            <button
                              type="button"
                              className="justo-followup__icon-btn"
                              title={strings.viewfollowup}
                              aria-label={strings.viewfollowup}
                              onClick={() =>
                                navigate(`/viewfollowup/${item._id}`)
                              }
                            >
                              <i className="fa fa-eye" aria-hidden="true" />
                            </button>
                          ) : null}
                          {!isAdmin &&
                          edit &&
                          item.followup_status === "Next day call" ? (
                            <button
                              type="button"
                              className="justo-followup__icon-btn"
                              title="Edit Followup"
                              aria-label="Edit Followup"
                              onClick={() =>
                                navigate(`/editfollowup/${item._id}`)
                              }
                            >
                              <i className="fa fa-pencil" aria-hidden="true" />
                            </button>
                          ) : null}
                          {view ? (
                            <button
                              type="button"
                              className="justo-followup__icon-btn"
                              title={strings.viewHistory}
                              aria-label={strings.viewHistory}
                              onClick={() =>
                                navigate(`/allfollowup/${item.lead_id}`)
                              }
                            >
                              <i className="fa fa-history" aria-hidden="true" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TableDataNotFound data={followupListData} />
            </div>
            <Pagination
              totalCount={totalCount}
              listData={followupListData}
              onPageChange={handlePageClick}
              itemOffset={page}
              setItemOffset={setPage}
              itemsPerPage={PAGE_SIZE}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FollowUp;
